import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";

import { traerPersonal } from "../services/rrhh";
import { parseSeguimientos } from "../shared/artSeguimiento";
import {
  traerSeguimientoArt,
  guardarSeguimientoArt,
} from "../services/artSeguimiento";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";

/* Seguimiento ART (P-173, Natalia): subir el export del portal y verlo por
 * persona.
 *
 * El archivo «ObservacionesYSeguimientos.xls» es una tabla HTML y NO trae la
 * identidad del trabajador, así que la persona se elige acá con el buscador y
 * el DNI se pega a cada fila. El parseo vive en `shared/artSeguimiento`; esta
 * pantalla sólo elige, previsualiza y guarda. Dato médico → ver.rrhh para ver,
 * cargar.rrhh para subir.
 */

const fecha = (v) => {
  const s = String(v ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "—";
  const [a, m, d] = s.split("-");
  return `${d}/${m}/${a}`;
};

const ArtSeguimiento = () => {
  const { permisos = null } = useOutletContext() || {};
  const permitido =
    permisos !== null && permisos.includes("ver.rrhh");
  const puedeCargar = permisos !== null && permisos.includes("cargar.rrhh");

  const [personal, setPersonal] = useState([]);
  const [buscar, setBuscar] = useState("");
  const [dni, setDni] = useState("");
  const [nombre, setNombre] = useState("");

  const [datos, setDatos] = useState(undefined); // undefined = cargando
  const [previa, setPrevia] = useState([]); // filas parseadas del archivo
  const [estado, setEstado] = useState(""); // "" | yendo | mal:<msg> | ok:<msg>
  const fileRef = useRef(null);

  useEffect(() => {
    if (!permitido) return;
    traerPersonal()
      .then(setPersonal)
      .catch(() => setPersonal([]));
  }, [permitido]);

  const candidatos = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    if (q.length < 2 || dni) return [];
    return personal
      .filter((c) =>
        [c.apellido, c.nombres, c.dni, c.legajo]
          .map((v) => String(v ?? "").toLowerCase())
          .some((v) => v.includes(q)),
      )
      .slice(0, 8);
  }, [personal, buscar, dni]);

  const cargarDatos = (d) => {
    setDatos(undefined);
    traerSeguimientoArt(d)
      .then(setDatos)
      .catch(() => setDatos(null));
  };

  const elegir = (c) => {
    setDni(String(c.dni));
    setNombre(`${c.apellido}, ${c.nombres}`);
    setBuscar(`${c.apellido}, ${c.nombres}`);
    setPrevia([]);
    setEstado("");
    if (fileRef.current) fileRef.current.value = "";
    cargarDatos(String(c.dni));
  };

  const limpiarPersona = () => {
    setDni("");
    setNombre("");
    setBuscar("");
    setPrevia([]);
    setDatos(undefined);
    setEstado("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const onArchivo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => {
      const filas = parseSeguimientos(String(rd.result || ""));
      setPrevia(filas);
      setEstado(
        filas.length
          ? ""
          : "mal:No encontré la tabla de seguimientos en ese archivo. " +
              "¿Es el export «ObservacionesYSeguimientos» de la ART?",
      );
    };
    rd.onerror = () =>
      setEstado("mal:No pude leer el archivo. Probá de nuevo.");
    rd.readAsText(f);
  };

  const guardar = async () => {
    if (!dni) return setEstado("mal:Elegí primero a la persona.");
    if (previa.length === 0) return setEstado("mal:Subí el archivo de la ART.");
    setEstado("yendo");
    try {
      const r = await guardarSeguimientoArt(dni, previa);
      setPrevia([]);
      if (fileRef.current) fileRef.current.value = "";
      const nuevas = r.insertados;
      const repetidas = r.total - r.insertados;
      setEstado(
        "ok:" +
          (nuevas === 0
            ? "Ya estaba todo cargado, no había nada nuevo."
            : `Guardé ${nuevas} ${nuevas === 1 ? "seguimiento" : "seguimientos"}` +
              (repetidas ? ` (${repetidas} ya estaban)` : "") + "."),
      );
      cargarDatos(dni);
    } catch (err) {
      setEstado(
        "mal:" +
          (/policy|permission|denied|violates/i.test(err?.message || "")
            ? "No se cargó: no tenés permiso."
            : `No se pudo guardar: ${err?.message || "error"}`),
      );
    }
  };

  if (permisos !== null && !permitido) {
    return <PuertaCerrada />;
  }

  return (
    <>
      <p className="panel-aviso suave">
        Subí el export <b>«ObservacionesYSeguimientos»</b> que bajás del portal
        de la ART. Como el archivo no dice de quién es, primero elegí a la
        persona y después subí el archivo — el seguimiento queda asociado a
        ella. Re-subir el mismo archivo no duplica.
      </p>

      {/* La persona */}
      <div className="tk-filtros" style={{ rowGap: 8 }}>
        <div style={{ position: "relative" }}>
          <input
            type="search"
            placeholder="Persona (nombre, DNI o legajo)"
            value={buscar}
            onChange={(e) => {
              setBuscar(e.target.value);
              if (dni) limpiarPersona();
            }}
            aria-label="Buscar a la persona"
          />
          {candidatos.length > 0 && (
            <div className="lic-sugeridos">
              {candidatos.map((c) => (
                <button key={c.dni} type="button" onClick={() => elegir(c)}>
                  {c.apellido}, {c.nombres}{" "}
                  <span className="sub">
                    DNI {c.dni}
                    {c.legajo ? ` · leg ${c.legajo}` : ""}
                    {c.es_chofer ? " · chofer" : ""}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        {dni && (
          <span className="pill" style={{ alignSelf: "center" }}>
            {nombre} · DNI {dni}
          </span>
        )}
      </div>

      {/* Subir el archivo (sólo quien puede cargar) */}
      {dni && puedeCargar && (
        <div className="tk-filtros" style={{ rowGap: 8, marginTop: 6 }}>
          <input
            ref={fileRef}
            type="file"
            accept=".xls,.html,.htm,text/html"
            onChange={onArchivo}
            aria-label="Archivo de la ART"
          />
          {previa.length > 0 && (
            <button
              type="button"
              className="btn-primario"
              onClick={guardar}
              disabled={estado === "yendo"}
            >
              {estado === "yendo"
                ? "Guardando…"
                : `Guardar ${previa.length} seguimiento${previa.length === 1 ? "" : "s"}`}
            </button>
          )}
          {estado.startsWith("mal:") && (
            <span className="pill mal">{estado.slice(4)}</span>
          )}
          {estado.startsWith("ok:") && (
            <span className="pill ok">{estado.slice(3)}</span>
          )}
        </div>
      )}

      {dni && !puedeCargar && (
        <p className="panel-aviso suave">
          Podés mirar, pero no cargar.
        </p>
      )}

      {/* Previsualización de lo que se va a guardar */}
      {previa.length > 0 && (
        <>
          <h3 style={{ marginTop: 20 }}>
            Se va a guardar · {previa.length} fila(s)
          </h3>
          <TablaSeguimiento filas={previa} />
        </>
      )}

      {/* Lo ya cargado de esta persona */}
      {dni && datos !== undefined && (
        <>
          <h3 style={{ marginTop: 24 }}>Seguimiento cargado</h3>
          {datos === null ? (
            <p className="panel-aviso__chico">No se pudo consultar.</p>
          ) : datos.length === 0 ? (
            <p className="panel-aviso__chico">
              Todavía no hay seguimiento cargado para {nombre}.
            </p>
          ) : (
            <TablaSeguimiento filas={datos} />
          )}
        </>
      )}
    </>
  );
};

const TablaSeguimiento = ({ filas }) => (
  <div className="tabla-wrap">
    <table className="tabla fija">
      <thead>
        <tr>
          <th style={{ width: 110 }}>Fecha</th>
          <th style={{ width: 150 }}>Motivo</th>
          <th style={{ width: 150 }}>Diagnóstico</th>
          <th>Evolución</th>
        </tr>
      </thead>
      <tbody>
        {filas.map((f, i) => (
          <tr key={f.id ?? `${f.firma || i}`}>
            <td>{f.fecha_turno ? fecha(f.fecha_turno) : f.fecha_texto || "—"}</td>
            <td>{f.motivo || "—"}</td>
            <td>{f.diagnostico || <span className="tenue">—</span>}</td>
            <td style={{ whiteSpace: "pre-wrap" }}>{f.evolucion || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default ArtSeguimiento;
